import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { ERROR_CODE } from "~/api";
import { Button } from "~/components";
import { ROUTES } from "~/router";
import { loginEmailSchema } from "~/screens/Auth/schemas/login.schema";
import { setPreviousLocation } from "~/shared/utils/navigationState.ts";
import { useUserStore } from "~/stores";
import { Input } from "~/ui";
import { useCanLogin } from "../hooks/useCanLogin";
import { useSendOtp } from "../hooks/useSendOtp";

type FormEmailSchema = z.infer<typeof loginEmailSchema>;

const LoginForm = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const [userEmail, setUserEmail] = useState(email ?? "");
  const encodedEmail = encodeURIComponent(userEmail);
  const [triggerCanLoginCheck, setTriggerCanLoginCheck] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormEmailSchema>({
    resolver: zodResolver(loginEmailSchema),
    defaultValues: {
      email: userEmail ?? "",
    },
  });

  const { data: canLoginResponse, isLoading: isCheckingCanLogin } = useCanLogin(
    encodedEmail,
    triggerCanLoginCheck,
  );

  const { mutate: sendOtp, isPending: isSendingOtp } = useSendOtp();

  const navigate = useNavigate();
  const location = useLocation();
  setPreviousLocation(location);

  useEffect(() => {
    if (triggerCanLoginCheck === 0) {
      return;
    }

    if (!userEmail || isCheckingCanLogin) {
      return;
    }

    if (canLoginResponse?.canLogin) {
      sendOtp({ email: userEmail, searchParams });
    } else if (
      canLoginResponse?.reason === ERROR_CODE.pendingEmailVerification &&
      canLoginResponse?.hash
    ) {
      const setHashedUserId = useUserStore.getState().setHashedUserId;
      setHashedUserId(canLoginResponse.hash);

      navigate(ROUTES.pendingVerification, { state: { email: userEmail } });
    }
  }, [canLoginResponse, isCheckingCanLogin, triggerCanLoginCheck]);

  const onSubmit = (data: FormEmailSchema) => {
    if (isSendingOtp || isCheckingCanLogin) {
      return;
    }
    if (data.email) {
      setUserEmail(data.email);
      setTriggerCanLoginCheck((prev) => prev + 1);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
      <Input
        id="email"
        required
        label="Email"
        placeholder="example@email.com"
        autoCorrect="off"
        autoComplete="email"
        spellCheck="false"
        {...register("email")}
        error={errors.email?.message}
      />

      <Button size="default" type="submit" className="w-full md:w-auto">
        Continue
      </Button>
    </form>
  );
};

export default LoginForm;
